import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft, Send, Plus, Trash2, GripVertical, ShieldCheck } from 'lucide-react'
import { contractsApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { SignerRequest, SignerType } from '@/types'

interface SignerFormData {
  name: string
  email: string
  signer_type?: SignerType
}

export default function ContractSendPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const [signers, setSigners] = useState<SignerFormData[]>([])
  const [newSigner, setNewSigner] = useState<SignerFormData>({
    name: '',
    email: '',
    signer_type: 'CLIENT',
  })
  const [message, setMessage] = useState('')
  const [expiresAt, setExpiresAt] = useState('')

  const { data: contract, isLoading } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => contractsApi.getById(id!),
    enabled: !!id,
  })

  // Pre-populate signers from contract if they exist
  useEffect(() => {
    if (contract?.signers && contract.signers.length > 0) {
      setSigners(contract.signers.map(s => ({
        name: s.name,
        email: s.email,
        signer_type: s.signer_type,
      })))
    }
  }, [contract])

  const sendMutation = useMutation({
    mutationFn: () => {
      const signerRequests: SignerRequest[] = signers.map((s, i) => ({
        name: s.name,
        email: s.email,
        signer_type: s.signer_type,
        signing_order: i + 1,
        verification_required: true, // OTP is mandatory for all signers
        verification_method: 'EMAIL_OTP',
      }))
      return contractsApi.sendForSigning(id!, {
        signers: signerRequests,
        message: message || undefined,
        expires_at: expiresAt || undefined,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      queryClient.invalidateQueries({ queryKey: ['contract', id] })
      toast.success('Contract sent for signing!')
      navigate(`/contracts/${id}`)
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to send contract'))
    },
  })

  const addSigner = () => {
    if (newSigner.name && newSigner.email) {
      setSigners([...signers, { ...newSigner }])
      setNewSigner({
        name: '',
        email: '',
        signer_type: 'CLIENT',
      })
    } else {
      toast.error('Please enter name and email')
    }
  }

  const removeSigner = (index: number) => {
    setSigners(signers.filter((_, i) => i !== index))
  }

  const handleSend = () => {
    if (signers.length === 0) {
      toast.error('Please add at least one signer')
      return
    }
    sendMutation.mutate()
  }

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>
  }

  if (!contract) {
    return <div className="text-center py-8">Contract not found</div>
  }

  if (contract.status !== 'DRAFT') {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">This contract has already been sent for signing.</p>
        <Button onClick={() => navigate(`/contracts/${id}`)}>View Contract</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/contracts/${id}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Send for Signing</h1>
          <p className="text-muted-foreground">{contract.title}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Signers</CardTitle>
          <CardDescription>Add the people who need to sign this contract. They will be notified in order.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {signers.map((signer, index) => (
            <div key={index} className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
              <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
              <div className="flex-1 grid gap-4 md:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{signer.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{signer.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">{signer.signer_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Verification</p>
                  <p className="font-medium flex items-center gap-1">
                    <ShieldCheck className="h-4 w-4 text-green-600" />
                    <span className="text-green-600">OTP Required</span>
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => removeSigner(index)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}

          <div className="flex gap-4 items-end p-4 border rounded-lg border-dashed">
            <div className="flex-1 space-y-2">
              <Label>Name</Label>
              <Input
                value={newSigner.name}
                onChange={(e) => setNewSigner({ ...newSigner, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="flex-1 space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={newSigner.email}
                onChange={(e) => setNewSigner({ ...newSigner, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div className="w-[150px] space-y-2">
              <Label>Type</Label>
              <Select
                value={newSigner.signer_type}
                onValueChange={(v) => setNewSigner({ ...newSigner, signer_type: v as SignerType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CLIENT">Client</SelectItem>
                  <SelectItem value="AGENCY">Agency</SelectItem>
                  <SelectItem value="WITNESS">Witness</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="button" onClick={addSigner}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Security & Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* OTP Verification - Always Enabled (Mandatory) */}
          <div className="flex items-center justify-between p-4 border rounded-lg bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/50 rounded-lg">
                <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <Label className="text-base font-medium text-green-700 dark:text-green-300">
                  Email OTP Verification Enabled
                </Label>
                <p className="text-sm text-green-600/80 dark:text-green-400/80">
                  All signers must verify their identity with a one-time code sent to their email before viewing and signing the document
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-sm font-medium">Mandatory</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message to Signers (optional)</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal message to include in the signing request email..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expires_at">Expiration Date (optional)</Label>
            <Input
              id="expires_at"
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              If set, the signing links will expire after this date.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={() => navigate(`/contracts/${id}`)}>
          Cancel
        </Button>
        <Button onClick={handleSend} disabled={sendMutation.isPending || signers.length === 0}>
          <Send className="mr-2 h-4 w-4" />
          {sendMutation.isPending ? 'Sending...' : 'Send for Signing'}
        </Button>
      </div>
    </div>
  )
}

